import { useState } from 'react';
import { Switch, Tooltip, Modal, message, Spin, Alert } from 'antd';
import {
	PlusOutlined,
	EditOutlined,
	DeleteOutlined,
	SyncOutlined,
	CopyOutlined,
	ApiOutlined,
	ExportOutlined,
} from '@ant-design/icons';
import * as claus from 'src/_shared/services/claus/endpoints';
import { useOrgIntegrations } from './hooks/useClaus';
import DuplicateIntegrationDialog from './dialogs/DuplicateIntegrationDialog.jsx';
import PromoteIntegrationDialog from './dialogs/PromoteIntegrationDialog.jsx';

export function IntegrationList({ orgId, orgExists, company, onCreate, onEdit }) {
	const { loading, data, error, reload } = useOrgIntegrations(orgExists ? orgId : null);
	const [busyId, setBusyId] = useState(null);
	const [dupFor, setDupFor] = useState(null);
	const [promoteFor, setPromoteFor] = useState(null);

	const items = data?.items || (Array.isArray(data) ? data : []);

	async function toggleEnabled(row, enabled) {
		if (busyId) return; // one mutation at a time — concurrent PATCH+reload pairs can interleave a stale reload
		setBusyId(row.id);
		try {
			await claus.patchIntegration(orgId, row.id, { enabled });
			message.success(`${row.name} ${enabled ? 'enabled' : 'disabled'}`);
			await reload();
		} catch (e) {
			message.error(e.message || 'Could not update the integration.');
		} finally {
			setBusyId(null);
		}
	}

	async function runSync(row) {
		if (busyId) return;
		setBusyId(row.id);
		try {
			const res = await claus.triggerSync(orgId, row.id);
			message.success(res?.syncHistoryId ? `Sync started (${res.syncHistoryId})` : 'Sync started');
		} catch (e) {
			message.error(e.message || 'Could not start the sync.');
		} finally {
			setBusyId(null);
		}
	}

	function confirmDelete(row) {
		Modal.confirm({
			title: `Delete ${row.name}?`,
			content: 'This removes the integration from CLAUS (schedule + credentials). This cannot be undone.',
			okText: 'Delete',
			okButtonProps: { danger: true },
			onOk: async () => {
				try {
					await claus.deleteIntegration(orgId, row.id);
					message.success('Integration deleted');
					await reload();
				} catch (e) {
					message.error(e.message || 'Could not delete the integration.');
				}
			},
		});
	}

	return (
		<>
			{error && (
				<Alert
					style={{ marginBottom: 12 }}
					type="error"
					showIcon
					message="Could not load integrations"
					description={error.message}
				/>
			)}

			{loading ? (
				<div className="js-loading">
					<Spin />
				</div>
			) : items.length === 0 ? (
				<div className="ei-empty">
					<div className="ei-empty-icon">
						<ApiOutlined />
					</div>
					<div className="ei-empty-title">No integrations yet</div>
					<div className="ei-empty-sub">
						Connect this company&apos;s ATS to start syncing jobs and candidates into Erin. You&apos;ll pick a
						source, add credentials, and map fields.
					</div>
					<button type="button" className="js-btn js-btn-primary" onClick={onCreate}>
						<PlusOutlined /> Create integration
					</button>
				</div>
			) : (
				<div className="ei-list">
					<div className="ei-listhead">
						<span className="js-eyebrow" style={{ margin: 0 }}>
							Configured
						</span>
						<span className="ei-count">
							{items.length} {items.length === 1 ? 'integration' : 'integrations'}
						</span>
					</div>
					{items.map((row) => (
						<div className="ei-row" key={row.id}>
							<div className="ei-row-lead">
								<ApiOutlined />
							</div>
							<div className="ei-row-main">
								<div className="ei-row-name">
									{row.name}
									<span className="js-badge">{row.type}</span>
									<span className={`ei-chip ${row.enabled ? 'is-on' : 'is-off'}`}>
										{row.enabled ? 'Enabled' : 'Disabled'}
									</span>
								</div>
								<div className="ei-row-meta">source {row.source}</div>
							</div>
							<div className="ei-row-actions">
								<Tooltip title={row.enabled ? 'Disable' : 'Enable'}>
									<Switch
										checked={row.enabled}
										loading={busyId === row.id}
										onChange={(v) => toggleEnabled(row, v)}
									/>
								</Tooltip>
								<Tooltip title="Run sync now">
									<button
										type="button"
										className="js-iconbtn"
										disabled={busyId === row.id || row.source === 'sftp'}
										onClick={() => runSync(row)}
										aria-label="Run sync"
									>
										<SyncOutlined />
									</button>
								</Tooltip>
								<Tooltip title="Duplicate">
									<button
										type="button"
										className="js-iconbtn"
										onClick={() => setDupFor(row)}
										aria-label="Duplicate"
									>
										<CopyOutlined />
									</button>
								</Tooltip>
								<Tooltip title="Promote to another company">
									<button
										type="button"
										className="js-iconbtn"
										onClick={() => setPromoteFor(row)}
										aria-label="Promote to another company"
									>
										<ExportOutlined />
									</button>
								</Tooltip>
								<Tooltip title="Edit">
									<button type="button" className="js-iconbtn" onClick={() => onEdit(row.id)} aria-label="Edit">
										<EditOutlined />
									</button>
								</Tooltip>
								<Tooltip title="Delete">
									<button
										type="button"
										className="js-iconbtn js-danger"
										onClick={() => confirmDelete(row)}
										aria-label="Delete"
									>
										<DeleteOutlined />
									</button>
								</Tooltip>
							</div>
						</div>
					))}
				</div>
			)}

			<DuplicateIntegrationDialog
				orgId={orgId}
				integration={dupFor}
				onClose={() => setDupFor(null)}
				onDuplicated={async () => {
					setDupFor(null);
					await reload();
				}}
			/>

			<PromoteIntegrationDialog
				sourceOrgId={orgId}
				sourceCompany={company}
				integration={promoteFor}
				onClose={() => setPromoteFor(null)}
			/>
		</>
	);
}

export default IntegrationList;
